import { zodResolver } from '@hookform/resolvers/zod';
import {
  Box,
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from '@mui/material';
import React from 'react';
import { Controller, useForm } from 'react-hook-form';
import { z } from 'zod';
import { Room, RoomType } from '../../types';
import { roomTypeLabels, roomTypes } from '../mockData/rooms';

const roomSchema = z.object({
  name: z.string().min(1, 'Room name is required'),
  roomNumber: z.string().min(1, 'Room number is required'),
  roomType: z.enum(roomTypes as [RoomType, ...RoomType[]]),
  capacity: z.number().min(0, 'Capacity must be 0 or greater'),
  accessibility: z.enum(['wheelchair', 'standard', 'limited']),
  hasFixedTeacher: z.boolean(),
  isShared: z.boolean(),
  isNomadic: z.boolean(),
});

type RoomFormData = z.infer<typeof roomSchema>;

interface RoomFormProps {
  room?: Room;
  floorId: string;
  onSubmit: (data: RoomFormData & { floorId: string }) => void;
  onCancel?: () => void;
}

export const RoomForm: React.FC<RoomFormProps> = ({ room, floorId, onSubmit, onCancel }) => {
  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<RoomFormData>({
    resolver: zodResolver(roomSchema),
    defaultValues: {
      name: room?.name || '',
      roomNumber: room?.roomNumber || '',
      roomType: room?.roomType || 'classroom',
      capacity: room?.capacity || 30,
      accessibility: room?.accessibility || 'standard',
      hasFixedTeacher: room?.usage.hasFixedTeacher || false,
      isShared: room?.usage.isShared || false,
      isNomadic: room?.usage.isNomadic || false,
    },
  });

  const onFormSubmit = (data: RoomFormData) => {
    onSubmit({ ...data, floorId });
  };

  return (
    <Box
      component="form"
      onSubmit={handleSubmit(onFormSubmit)}
      sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}
    >
      <Controller
        name="name"
        control={control}
        render={({ field }) => (
          <TextField {...field} label="Room Name" error={!!errors.name} helperText={errors.name?.message} fullWidth />
        )}
      />

      <Controller
        name="roomNumber"
        control={control}
        render={({ field }) => (
          <TextField
            {...field}
            label="Room Number"
            error={!!errors.roomNumber}
            helperText={errors.roomNumber?.message}
            fullWidth
          />
        )}
      />

      <Controller
        name="roomType"
        control={control}
        render={({ field }) => (
          <FormControl fullWidth error={!!errors.roomType}>
            <InputLabel>Room Type</InputLabel>
            <Select {...field} label="Room Type">
              {roomTypes.map((type) => (
                <MenuItem key={type} value={type}>
                  {roomTypeLabels[type]}
                </MenuItem>
              ))}
            </Select>
            {errors.roomType && <FormHelperText>{errors.roomType.message}</FormHelperText>}
          </FormControl>
        )}
      />

      <Controller
        name="capacity"
        control={control}
        render={({ field }) => (
          <TextField
            {...field}
            label="Capacity"
            type="number"
            error={!!errors.capacity}
            helperText={errors.capacity?.message}
            fullWidth
            onChange={(e) => field.onChange(Number.parseInt(e.target.value) || 0)}
          />
        )}
      />

      <Controller
        name="accessibility"
        control={control}
        render={({ field }) => (
          <FormControl fullWidth error={!!errors.accessibility}>
            <InputLabel>Accessibility</InputLabel>
            <Select {...field} label="Accessibility">
              <MenuItem value="wheelchair">Wheelchair Access</MenuItem>
              <MenuItem value="standard">Standard</MenuItem>
              <MenuItem value="limited">Limited</MenuItem>
            </Select>
            {errors.accessibility && <FormHelperText>{errors.accessibility.message}</FormHelperText>}
          </FormControl>
        )}
      />

      <Box>
        <Controller
          name="hasFixedTeacher"
          control={control}
          render={({ field }) => (
            <FormControlLabel control={<Checkbox {...field} checked={field.value} />} label="Has Fixed Teacher" />
          )}
        />

        <Controller
          name="isShared"
          control={control}
          render={({ field }) => (
            <FormControlLabel control={<Checkbox {...field} checked={field.value} />} label="Shared Room" />
          )}
        />

        <Controller
          name="isNomadic"
          control={control}
          render={({ field }) => (
            <FormControlLabel control={<Checkbox {...field} checked={field.value} />} label="Nomadic (Teacher moves)" />
          )}
        />
      </Box>

      <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
        {onCancel && (
          <Button onClick={onCancel} variant="outlined">
            Cancel
          </Button>
        )}
        <Button type="submit" variant="contained">
          {room ? 'Update Room' : 'Add Room'}
        </Button>
      </Box>
    </Box>
  );
};

export default RoomForm;
